import { useEffect, useState } from "react";
import { useForm } from "react-hook-form";
import api from "../../../services/api";
import {pxToCm } from "../../../utils/constants";
import { Toast } from "../../../shared/Toast";
import { debouncePromise } from "../../../utils/Debounce";
import config from "../../../config";

function classifyAd(pageWidth, pageHeight, adWidth, adHeight, pageTypeValue , denom) {
  if (pageWidth <= 0 || pageHeight <= 0 || adWidth <= 0 || adHeight <= 0) {
    return "any_size";
  }
  if (["j1", "j2", "j3"].includes(pageTypeValue)) {
    return pageTypeValue;
  }
  const aspect = pxToCm(adWidth,denom) / pxToCm(adHeight,denom);
  const inverseAspect = pxToCm(adHeight,denom) / pxToCm(adWidth,denom);
  if (aspect >= 4 || inverseAspect >= 4) {
    return "skybus";
  }
  const pageArea = pxToCm(pageWidth,denom) * pxToCm(pageHeight,denom);
  const adArea = pxToCm(adWidth,denom) * pxToCm(adHeight,denom);
  const areaRatio = adArea / pageArea;
  if (areaRatio >= 0.6) return "full_page_ad";
  if (areaRatio >= 0.45) return "half_page_ad";
  if (areaRatio >= 0.25) return "quarter_page_ad";
  return "any_size";
}

export const useEditAdLogic = ({ onClose, preFillData, croppedCanvas, originalUrls = [] }) => {
  const [loading, setLoading] = useState(false);
  const [uploading, setUploading] = useState(false);
  const [uploadedUrl, setUploadedUrl] = useState("");
  const [status, setStatus] = useState(1);
  const [showOriginal, setShowOriginal] = useState(false);
  const [inputFields, setInputFields] = useState([{ person_name: "", relation: 5, contact: "", address: "" }]);

  const {
    register,
    control,
    handleSubmit,
    watch,
    setValue,
    getValues,
    formState: { errors },
  } = useForm({
    defaultValues: {
      date: "",
      language: null,
      newspaper: null,
      page_no: null,
      edition: null,
      location_name: null,
      page_type: null,
      ad_format: null,
      campaign_name: "",
      brand_type: "finite",
      category_id: null,
      brand_location: null,
      brand_name: null,
      estimated_value: "",
      advertisers: [],
      subcategory_id: null,
    },
  });

  const watchEdition = watch("edition");
  const watchBrandType = watch("brand_type");
  const watchLanguage = watch("language");
  const watchAdFormat = watch("ad_format");
  const watchLocationName = watch("location_name");
  const watchPageType = watch("page_type");
  const watchBrandLocation = watch("brand_location");
  const watchBrandName = watch("brand_name");
  const watchNewspaper = watch("newspaper");

  const [newspapers, setNewspapers] = useState([]);
  const [editions, setEditions] = useState([]);
  const [locationNames, setLocationNames] = useState([]);
  const [pageTypes, setPageTypes] = useState([]);
  const [adFormats, setAdFormats] = useState([]);
  const [categories, setCategories] = useState([]);
  const [brandlocations, setBrandlocations] = useState([]);
  const [color,setColor]=useState(1);
  const [estimated_value,setEstimatedValue] = useState(0)
  const [subCategories, setSubCategories] = useState([]);
  const [denom,setDenom] = useState(62);
  const [submitError, setSubmitError] = useState(null);
  const [showErrorModal, setShowErrorModal] = useState(false);

  const handleCloseErrorModal = () => {
    setShowErrorModal(false);
    setSubmitError(null);
  };

  useEffect(() => {
    const load = async () => {
      const [brandRes, newsRes] = await Promise.all([
        api({ url: "/birdview/brandsLocations", method: "POST", data: {} }),
        api({ url: "/market/newspapers", method: "POST", data: {} }),
      ]);
      setBrandlocations(brandRes?.data?.result || []);
      if (newsRes?.code === 200) {
        setNewspapers(
          newsRes.data
            .filter((n) => n.name.toLowerCase() !== "way2news")
            .map((n) => ({
              value: n.name.toLowerCase() === "dinathanthi" ? "dailythanthi" : n.id,
              label: n.name.toLowerCase() === "dinathanthi" ? "Dailythanthi" : n.name,
            }))
        );
      }
    };
    load();
  }, []);
  
  const loadPageTypes = async () => {
  const [pageRes, adRes] = await Promise.all([
    api({ url: "/eenenadupagerates/getpage_types", method: "POST",data:{newspaper:watchNewspaper?.value} }),
    api({ url: "/eenenadupagerates/getad_types", method: "POST",data:{newspaper:watchNewspaper?.value} }),
  ]);
  setPageTypes(pageRes?.data || []);
  setAdFormats(adRes?.data || []);
  }
  useEffect(()=>{
    if(watchNewspaper?.value){
    loadPageTypes();
  }
},[watchNewspaper]);
  

  useEffect(()=>{
      const val = (watchEdition?.value === 'main' ? config.MAIN_CROP_RATIO : config.OTHER_CROP_RATIO);
      if(!((watchEdition?.value || "").includes("main"))) setValue('page_type',{"label": "display_ad","value": "display_ad" })
      if(!((watchEdition?.value || "").includes("main"))) setValue('ad_format',{"label": "any_size","value": "any_size" })
      setDenom(val);
  },[watchEdition])

  useEffect(() => {
    if (!preFillData) return;

    setValue("date", preFillData.date || "");
    setValue("language", preFillData.language || null);
    setValue("newspaper", preFillData.newspaper || null);
    setValue("edition", preFillData?.pageName  || null);
    setValue("page_no", preFillData.page_no ? Number(preFillData.page_no) : null);
    setValue("page_type", preFillData.page_type || null);

    if (preFillData.district?.value && preFillData.district?.label) {
      const districtObj = { value: preFillData.district.value, label: preFillData.district.label };
      
      setValue("location_name", districtObj || preFillData.edition);
      setValue("brand_location", districtObj || preFillData.edition);
    }
  }, [preFillData, setValue]);

  useEffect(() => {
    if (originalUrls?.length > 0 && croppedCanvas) {
      const img = new Image();
      img.onload = () => {
        const detected = classifyAd(img.width, img.height, croppedCanvas.width, croppedCanvas.height, watchPageType?.value,denom);
        const formatOption = adFormats.find((f) => f.label === detected);
        if (formatOption) setValue("ad_format", formatOption);
      };
      img.src = originalUrls[0];
    }
  }, [originalUrls, croppedCanvas, watchPageType, adFormats, setValue]);

  useEffect(() => {
    if (watchBrandName?.label && watchAdFormat?.label) {
      const name = watchBrandName.label.trim().replace(/\s+/g, '_');
      const campaign = `${name}_${watchAdFormat.label}`;    
      const limited = campaign.slice(0, 20);
      setValue("campaign_name", limited);
    }
  }, [watchBrandName, watchAdFormat, setValue]);

  useEffect(() => {
    if (watch("newspaper") && watch("language")) {
      api({
        url: "/birdview/getPageNames",
        method: "POST",
        data: { lang_id: watch("language").value },
      }).then((r) => setEditions(r?.data || []));
    }
  }, [watch("newspaper"), watch("language")]);

  useEffect(() => {
    if (watchEdition?.value && watchNewspaper?.value) {
      api({
        url: "/eenenadupagerates/getlocations",
        method: "POST",
        data: { location_type: watchEdition.value,newspaper:watchNewspaper?.value},
      }).then((r) => {
        setLocationNames(r?.data || []);
      });
    }
  }, [watchEdition?.value ,watchNewspaper?.value]);

  useEffect(() => {
    if (["finite", "infinite", "alpha"].includes(watchBrandType)) {
      api({
        url: "/birdview/alphacategory",
        method: "POST",
        data: { brandtype: watchBrandType },
      }).then((r) => setCategories(r?.data || []));
    }
  }, [watchBrandType]);

  useEffect(() => {
    const calc = async () => {
      if (watchEdition && watchPageType && watchAdFormat && croppedCanvas) {
        const area = pxToCm(croppedCanvas.width,denom) * pxToCm(croppedCanvas.height,denom);
        const res = await api({
          url: "/eenenadupagerates/estimatedvalue",
          method: "POST",
          data: {
            location_type: watchEdition.value,
            ad_location: watchLocationName?.value || null,
            page_type: watchPageType?.value,
            ad_type: watchAdFormat?.value,
            area,
            color,
            newspaper:watchNewspaper?.value
          },
        });
        if (res?.data?.estimated_value) {
          setEstimatedValue(res.data);
          setValue("estimated_value", String(res.data.estimated_value));
        }
      }
    };
    calc();
  }, [watchEdition, watchLocationName, watchPageType, watchAdFormat, croppedCanvas,color, setValue]);

  const loadBrands = async (input) => {
    if (!input || input.length < 3 || !watchBrandLocation?.value) return [];
    const res = await api({
      url: "/user/sales_persons/getBrands",
      method: "POST",
      data: { search: input, brand_location: watchBrandLocation.value },
    });
    return res?.data || [];
  };

  const handleUploadImage = async () => {
    if (!croppedCanvas) throw new Error("No cropped canvas available");
    if (uploadedUrl) return uploadedUrl;
    if (uploading) throw new Error("Upload already in progress");
    setUploading(true);
    try {
      const url = await new Promise((resolve, reject) => {
        croppedCanvas.toBlob(async (blob) => {
          const formData = new FormData();
          formData.append("files", blob, "cropped.png");
          try {
            const res = await api({ url: "/upload/birdviewimage", method: "POST", data: formData });
            if (res.status === "SUCCESS" && res.data?.data) resolve(res.data.data);
            else reject(new Error("Upload failed"));
          } catch (error) {
            reject(error);
          }
        }, "image/png");
      });
      setUploadedUrl(url);
      return url;
    } catch (error) {
      Toast("error", "Upload failed");
      throw error;
    } finally {
      setUploading(false);
    }
  };
  const watchCategoryId = watch("category_id");   

useEffect(() => {
   if((watchBrandType !== "alpha") || !watchCategoryId?.value) return;
   const fetchSubCategories = async() =>{
      const result = await api({
        url: "/birdview/alphasubcategory",
        method: "POST",
        data: { category_id: watchCategoryId.value },
      })
      if(result.status==="SUCCESS"){
         setSubCategories(result.data);
      }
   }
   fetchSubCategories();
}, [watchBrandType, watchCategoryId]);

  const onSubmit = async (data) => {
    if (!data.estimated_value || parseFloat(data.estimated_value) <= 0) {
      return Toast("error", "Estimated value must be greater than zero");
    }
    let imageUrl = uploadedUrl;
    if (!imageUrl && croppedCanvas) {
      try {
        imageUrl = await handleUploadImage();
      } catch {
        return;
      }
    }
    if (!imageUrl) return Toast("error", "Failed to upload image");

    const locationsArr = Array.isArray(data.location_name) ? data.location_name.map((l) => l.value) : data.location_name ? [data.location_name.value] : [];
    const area = pxToCm(croppedCanvas.width,denom) * pxToCm(croppedCanvas.height,denom);

    setLoading(true);
    try {
      const payload = {
        date: data.date,
        language: data.language?.value,
        newspaper_name: data.newspaper?.label,
        page_no: "" + data.page_no,
        edition: data.edition?.value,
        location: locationsArr,
        page_type: data.page_type?.value,
        ad_format: data.ad_format?.label,
        estimated_value: data.estimated_value,
        campaign_name: data.campaign_name,
        brand_type: data.brand_type,
        brand_name: data.brand_name?.value || null,
        category_id: data.category_id?.value || "",
        advertisers: data.brand_type === "alpha" ? JSON.stringify(inputFields) : "[]",
        upload_url: imageUrl,
        original_url: originalUrls,
        preFillData,
        status,
        dimensions : { x : croppedCanvas.x, y:croppedCanvas.y, px:croppedCanvas.width, py:croppedCanvas.height,  w : pxToCm(croppedCanvas.width,denom) , h : pxToCm(croppedCanvas.height,denom), color, area : area, price_per_unit : estimated_value?.price_per_unit, page_rate_id: estimated_value.id},
        sub_category_id:data.subcategory_id?.value,
        page_rate_id: estimated_value.id,
        event_date : data?.event_date
      };
      const res = await api({ url: "/birdview/addai", method: "POST", data: payload });
      if (res?.code === 200) {
        Toast("success", status === 1 ? "Ad saved" : "Ad rejected");
        onClose();
      } else {
        setSubmitError(res.data?.data || res.data);
        setShowErrorModal(true);
        Toast("error", res.message || "Failed to submit data");
      }
    } catch {
      Toast("error", "Submission failed");
    } finally {
      setLoading(false);
    }
  };

  return {
    register,
    control,
    handleSubmit,
    errors,
    newspapers,
    editions,
    locationNames,
    pageTypes,
    adFormats,
    categories,
    brandlocations,
    loadBrands: debouncePromise(loadBrands, 500),
    onSubmit,
    loading,
    uploading,
    setStatus,
    inputFields,
    setInputFields,
    showOriginal,
    setShowOriginal,
    watchBrandType,
    watchBrandLocation,
    color,
    setColor,
    subCategories,
    watchCategoryId,
    watchEdition,
    setValue,
    handleCloseErrorModal,
    submitError,
    showErrorModal
  };
};
